import React from 'react';
import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import StageStepper from '../../../shared/components/StageStepper';
import type { BaselineStackView } from '../../../types/backend';
import StatusChip from '../../../shared/components/StatusChip';
import InlineAlert from '../../../shared/components/InlineAlert';

import {
  convergedOnSeedRole,
  deriveOnboardingGates,
  stackReason,
  type GateState,
} from '../deriveOnboardingGates';
import type { StageStep } from '../../../shared/components/StageStepper';

/**
 * The strip is the same ladder as the dialog, condensed to labels.
 *
 * It has to be the same derivation and not a second one: when this panel had
 * its own, the dialog was rebuilt to show every step of the process and this
 * one was left collapsing to whatever had data, so it showed Registered and
 * then jumped to Baselines. Two renderings of one thing drift, and only one
 * of them gets fixed.
 */
const STRIP_STATE: Record<GateState, StageStep['state']> = {
  done: 'success',
  running: 'running',
  // Amber rather than red, and the same amber the rows below use: this state
  // is a stack stopped and waiting on a person, not something that broke.
  // Two colours for one condition read as two conditions.
  failed: 'warning',
  unwanted: 'warning',
  inconclusive: 'unknown',
  pending: 'skipped',
  unobserved: 'skipped',
};

function stripSteps(account: Parameters<typeof deriveOnboardingGates>[0], enabled: boolean): StageStep[] {
  return deriveOnboardingGates(account).map((gate) => ({
    key: gate.key,
    label: gate.title,
    // Only a step that needs a person says anything here; the rest are their
    // icon. An inactive account is waiting on a switch, so nothing below it
    // is really in progress.
    state: enabled ? STRIP_STATE[gate.state] : STRIP_STATE[gate.state] === 'running' ? 'skipped' : STRIP_STATE[gate.state],
    // A deploy gate's reason belongs to one of its stacks and is shown on
    // that row below, so repeating it here would print the same sentence
    // twice. A gate with no rows has nowhere else to say it.
    note: gate.targets.length > 0 ? undefined : gate.reason,
  }));
}
import { useAccountBaselineStatus } from '../hooks/useAccountBaselineStatus';
import AccountFlowModal from './AccountFlowModal';

export interface AccountOnboardingPanelProps {
  customerId: string;
  /** From the account record, which is the authority for it. */
  enabled?: boolean;
  /** The 12-digit AWS account id, which is how the status response keys accounts. */
  awsAccountId: string;
}

/**
 * Where one account is in onboarding: three stages, then the stacks behind the
 * third. Reads the `aws-account-baseline` reconciler's own per-account rows, not
 * `Deployment` rows, which answer a different question.
 */
/**
 * What this row says beyond the shared line: what collided, which is the part
 * that differs between rows. Without a shared line it carries the whole
 * reason, since nothing else would say it.
 *
 * Anything the shared sentence already names is dropped. The sentence lifted
 * above the list is one stack's, and when it lists that stack's own
 * collisions the row beneath it would otherwise print the same names twice,
 * one line apart. Dropping by what the sentence contains rather than by which
 * stack it came from keeps a row that has something left to say.
 */
function rowReason(stack: BaselineStackView, shared: SharedHalt | null): string | undefined {
  if (!shared || !isHalted(stack)) return stackReason(stack);
  const unsaid = (stack.refusals ?? []).filter((r) => !shared.reason.includes(r));
  return unsaid.join('; ') || undefined;
}


/** Stopped and needing a person, as opposed to working or settled. */
function isHalted(stack: BaselineStackView): boolean {
  return stack.status === 'REFUSED' || stack.status === 'BLOCKED';
}

/**
 * When several stacks stopped for the same reason, that reason is a fact
 * about the account rather than about each stack, and printing it on every
 * row buries the part that differs.
 *
 * The shared line is the backend's own sentence from the first such stack,
 * not something reworded here, and the rows then carry only what collided,
 * which is the half that is different per row.
 */
interface SharedHalt {
  reason: string;
  code: string;
}

function sharedHaltReason(stacks: BaselineStackView[]): SharedHalt | null {
  const halted = stacks.filter(isHalted);
  if (halted.length < 2) return null;
  const code = halted[0].blocked_reason;
  if (!code || halted.some((s) => s.blocked_reason !== code)) return null;
  const reason = halted[0].summary;
  return reason ? { reason, code } : null;
}

const AccountOnboardingPanel: React.FC<AccountOnboardingPanelProps> = ({
  customerId, awsAccountId, enabled = true,
}) => {
  const { accounts, loading, error } = useAccountBaselineStatus(customerId);
  const account = accounts.find((a) => a.aws_account_id === awsAccountId);
  const [flowOpen, setFlowOpen] = React.useState(false);

  if (error) {
    return (
      <Box sx={{ px: 3, pt: 2 }}>
        <InlineAlert severity="error">{error}</InlineAlert>
      </Box>
    );
  }

  if (!account) {
    // Still loading is a different fact from "the reconciler does not track
    // this account", and neither is an error.
    return (
      <Box sx={{ px: 3, pt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        {loading ? (
          <>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">Reading onboarding status</Typography>
          </>
        ) : (
          <Typography variant="caption" color="text.secondary">
            No onboarding status for this account yet.
          </Typography>
        )}
      </Box>
    );
  }

  const stacks = account.stacks ?? [];
  const shared = sharedHaltReason(stacks);

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          ONBOARDING
        </Typography>
        <Button size="small" onClick={() => setFlowOpen(true)}>
          View run
        </Button>
      </Box>

      {/*
        The stepper divides its width between the stages, so at the full width
        of this dialog three stages read as three unrelated labels rather than
        one sequence. Cap it at a width where the connectors still join them.
      */}
      <Box sx={{ maxWidth: 720 }}>
        <StageStepper steps={stripSteps(account, enabled)} ariaLabel="Account onboarding stages" />
      </Box>

      {shared && (
        <Box sx={{ mt: 1, px: 1.5, py: 1, border: 1, borderColor: 'warning.main', borderRadius: 1 }}>
          <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
            {shared.reason}
          </Typography>
        </Box>
      )}

      {stacks.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {stacks.map((stack) => (
            <Box
              key={`${stack.component_name}@${stack.region}`}
              sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1 }}
            >
              <StatusChip status={stack.status} />
              <Typography variant="body2" sx={{ minWidth: 0 }}>
                {stack.component_name}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                  {stack.region}
                </Typography>
              </Typography>
              {convergedOnSeedRole(stack) && (
                // Also shown per stack, because a refusal elsewhere in the
                // account takes the stage note and would otherwise hide this.
                <Typography variant="caption" color="warning.main" sx={{ whiteSpace: 'nowrap' }}>
                  via seed role
                </Typography>
              )}
              {/*
                One reason per row, in the words most use to a person: the
                sentence first, then what collided, and a code like
                RESOURCES_ALREADY_EXIST only when nothing better exists. A
                stack sitting in PENDING across ticks is a real state rather
                than a stall, and its summary is the only thing that says so.
              */}
              {rowReason(stack, shared) && (
                <Typography
                  variant="caption"
                  // Amber, not red: a stack held up by something an operator
                  // will deal with is a condition to act on, not an alarm.
                  color={isHalted(stack) ? 'warning.main' : 'text.secondary'}
                  sx={{ minWidth: 0 }}
                >
                  {rowReason(stack, shared)}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/*
        No liveness stage and no sentence about it. Nothing consumes the
        call-home event, so there is nothing to render, and the explanation
        belongs in the flow view rather than on a row meant to be scanned
        (plan A14, C7).
      */}
      <Divider sx={{ mt: 2 }} />

      {/*
        Mounted only while open: it reads the reconciler's resources, and a
        closed dialog should not be doing that for every account you click.
      */}
      {flowOpen && (
      <AccountFlowModal
        open={flowOpen}
        onClose={() => setFlowOpen(false)}
        awsAccountId={awsAccountId}
        accountName={account.account_name}
        account={account}
        enabled={enabled}
      />
      )}
    </Box>
  );
};

export default AccountOnboardingPanel;
